import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

function loadEnterpriseScript(sitekey) {
  return new Promise((resolve, reject) => {
    if (window.grecaptcha?.enterprise?.execute) {
      window.grecaptcha.enterprise.ready(() =>
        resolve(window.grecaptcha.enterprise)
      );
      return;
    }

    const SCRIPT_ID = "recaptcha-enterprise";
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      const onReady = () => {
        if (window.grecaptcha?.enterprise) {
          window.grecaptcha.enterprise.ready(() =>
            resolve(window.grecaptcha.enterprise)
          );
        } else {
          reject(new Error("reCAPTCHA Enterprise failed to load"));
        }
      };
      if (window.grecaptcha?.enterprise) {
        onReady();
      } else {
        existing.addEventListener("load", onReady, { once: true });
        existing.addEventListener(
          "error",
          () => reject(new Error("reCAPTCHA Enterprise script error")),
          { once: true }
        );
      }
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${sitekey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.grecaptcha.enterprise.ready(() =>
        resolve(window.grecaptcha.enterprise)
      );
    };
    script.onerror = () =>
      reject(new Error("reCAPTCHA Enterprise script error"));
    document.body.appendChild(script);
  });
}

const EnterpriseRecaptcha = forwardRef(function EnterpriseRecaptcha(
  { sitekey, onChange, onExpired, action = "REGISTER" },
  ref
) {
  const grecaptchaRef = useRef(null);
  const actionRef = useRef(action);
  const onChangeRef = useRef(onChange);

  actionRef.current = action;
  onChangeRef.current = onChange;

  const execute = async () => {
    const grecaptcha =
      grecaptchaRef.current || (await loadEnterpriseScript(sitekey));
    grecaptchaRef.current = grecaptcha;
    const token = await grecaptcha.execute(sitekey, {
      action: actionRef.current,
    });
    if (onChangeRef.current) onChangeRef.current(token);
    return token;
  };

  useImperativeHandle(ref, () => ({
    execute,
    reset: () => {
      if (onChangeRef.current) onChangeRef.current(null);
      if (onExpired) onExpired();
    },
  }));

  useEffect(() => {
    let cancelled = false;
    if (!sitekey) return undefined;

    loadEnterpriseScript(sitekey)
      .then((grecaptcha) => {
        if (cancelled) return;
        grecaptchaRef.current = grecaptcha;
        return execute();
      })
      .catch((err) => {
        console.error(err);
        if (onChangeRef.current) onChangeRef.current(null);
      });

    return () => {
      cancelled = true;
    };
  }, [sitekey, action]);

  return (
    <p className="recaptcha-note">
      Protected by reCAPTCHA Enterprise.
    </p>
  );
});

export default EnterpriseRecaptcha;
